import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, FileText } from 'lucide-react';
import supabase from '../../supabase';
import EmployeeInfo from './EmployeeInfo';
import EmpSalaryslip from './EmpSalarySlip';
import './EmployeeDetails.css';

const EmployeeDetails = ({ employeeId, onBack, modalState, openModal, closeModal }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employee-info');
  
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to create URL-friendly slug
  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Parse URL to get active tab
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // URL structure: /dashboard/team/view-{name}/{tab}
    // pathParts: ['dashboard', 'team', 'view-{name}', '{tab}']
    
    if (pathParts.length >= 4) {
      const tab = pathParts[3];
      if (tab === 'employee-info' || tab === 'payslips') {
        setActiveTab(tab);
      }
    } else {
      // Default to employee-info if no tab specified
      setActiveTab('employee-info');
    }
  }, [location.pathname]);

  // Fetch employee data
  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    }
  }, [employeeId]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      // First try to find by actual ID
      let { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('id', employeeId)
        .single();

      // If not found by ID, try to find by name slug
      if (error || !data) {
        const { data: allEmployees } = await supabase
          .from('employees')
          .select('*');
        
        if (allEmployees) {
          data = allEmployees.find(emp => {
            const slug = createSlug(emp.name);
            return slug === employeeId;
          });
        }
      }

      if (data) {
        setEmployee(data);
      }
    } catch (err) {
      console.error('Error fetching employee:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    onBack();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update URL with tab
    if (employee) {
      const slug = createSlug(employee.name);
      navigate(`/dashboard/team/view-${slug}/${tab}`);
    }
  };

  const handleEmployeeUpdate = (updatedEmployee) => {
    setEmployee(updatedEmployee);
  };

  const handleRefresh = () => {
    if (employeeId) {
      fetchEmployee();
    }
  };

  const generateFallbackAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Unknown')}&size=80&background=000000&color=ffffff&bold=true`;
  };

  if (loading) {
    return (
      <div className="empdetails-container">
        <div className="empdetails-loading">
          <div className="empdetails-spinner"></div>
          <p>Loading employee details...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="empdetails-container">
        <div className="empdetails-not-found">
          <h2>Employee Not Found</h2>
          <p>No employee data provided.</p>
          <button onClick={handleBack} className="empdetails-back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="empdetails-container">
      <div className="empdetails-wrapper">
        {/* Header */}
        <div className="empdetails-header">
          <div className="empdetails-employee-summary">
            <img
              src={employee.image || generateFallbackAvatar(employee.name)}
              alt={`${employee.name}'s profile`}
              className="empdetails-avatar"
              onError={(e) => { e.target.src = generateFallbackAvatar(employee.name); }}
            />
            <div className="empdetails-employee-basic">
              <h1 className="empdetails-employee-name">{employee.name}</h1>
              <p className="empdetails-employee-id">ID: {employee.emp_id || 'N/A'}</p>
            </div>
          </div>
          
          <button 
            onClick={handleBack} 
            className="empdetails-back-button"
            title="Back to Employee Dashboard"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="empdetails-tabs">
          <div className="empdetails-tab-list">
            <button
              className={`empdetails-tab ${activeTab === 'employee-info' ? 'empdetails-tab-active' : ''}`}
              onClick={() => handleTabChange('employee-info')}
            >
              <User size={18} />
              <span>Employee Info</span>
            </button>
            <button
              className={`empdetails-tab ${activeTab === 'payslips' ? 'empdetails-tab-active' : ''}`}
              onClick={() => handleTabChange('payslips')}
            >
              <FileText size={18} />
              <span>Payslips</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="empdetails-tab-content">
          {activeTab === 'employee-info' && (
            <EmployeeInfo 
              employee={employee} 
              onEmployeeUpdate={handleEmployeeUpdate}
              onRefresh={handleRefresh}
            />
          )}
          {activeTab === 'payslips' && (
            <EmpSalaryslip 
              employee={employee}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetails;